import React, { Component } from 'react';
import './DateRangePickerFilter.css';
import 'react-table/react-table.css';
import { I18n } from 'react-redux-i18n';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import actions from '../../../actions';
import Button from '../Button';
import Tooltip from '../Tooltip';
import DropdownMenu from '../Dropdown/DropdownMenu';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { DateRange, DefinedRange } from 'react-date-range';
import {
  lastWeek,
  lastMonth,
  formatDate,
  equalsDate,
} from '../../../utils/dates';
import localeEs from 'date-fns/locale/es';
import localePt from 'date-fns/locale/pt-BR';
import defaultLocale from 'date-fns/locale/en-US';
import dayjs from 'dayjs';
import {
  getFocusMonths,
  getMonthRange,
  getPanelBounds,
  isValidPanelMonths,
  isMonthOutOfBounds,
  nextMonth,
  startOfMonth,
} from './panelMonths';

const DEFAULT_START_DATE = '2019-12-29T00:00:00-03:00';

const toValidDate = (value, fallback = new Date()) => {
  // Avoid Date(null) => epoch (1969/1970 in UI depending on timezone)
  if (value === null || value === undefined || value === '') {
    return new Date(fallback);
  }

  const candidate =
    value instanceof Date
      ? new Date(value.getTime())
      : value && typeof value.toDate === 'function'
      ? value.toDate()
      : new Date(value);

  return candidate instanceof Date && !Number.isNaN(candidate.getTime())
    ? candidate
    : new Date(fallback);
};

const getDefaultStartDate = () => toValidDate(DEFAULT_START_DATE, new Date());
const getDefaultEndDate = () => new Date();

// Same markup as the library's month/year navigator, but month options that the
// panel cannot reach (outside minDate/maxDate) are disabled. Arrows are omitted
// because the styles hide them.
const renderMonthYearNavigator = (focusedDate, changeShownDate, props) => {
  const { minDate, maxDate, locale, ariaLabels } = props;
  const focusedYear = focusedDate.getFullYear();
  const years = [];
  for (
    let year = maxDate.getFullYear();
    year >= minDate.getFullYear();
    year--
  ) {
    years.push(year);
  }
  return (
    <div
      onMouseUp={(e) => e.stopPropagation()}
      className="rdrMonthAndYearWrapper"
    >
      <span className="rdrMonthAndYearPickers">
        <span className="rdrMonthPicker">
          <select
            value={focusedDate.getMonth()}
            onChange={(e) => changeShownDate(e.target.value, 'setMonth')}
            aria-label={ariaLabels.monthPicker}
          >
            {[...Array(12).keys()].map((month) => (
              <option
                key={month}
                value={month}
                disabled={isMonthOutOfBounds(
                  focusedYear,
                  month,
                  minDate,
                  maxDate,
                )}
              >
                {locale.localize.month(month)}
              </option>
            ))}
          </select>
        </span>
        <span className="rdrMonthAndYearDivider" />
        <span className="rdrYearPicker">
          <select
            value={focusedYear}
            onChange={(e) => changeShownDate(e.target.value, 'setYear')}
            aria-label={ariaLabels.yearPicker}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </span>
      </span>
    </div>
  );
};

class DateRangePickerFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filterValues: {
        dates: [
          {
            startDate: null,
            endDate: null,
            key: 'selection',
          },
        ],
      },
      closeAfter: false,
      hiddenPopup: false,
      query: false,
      openCalendar: false,
      filtered: false,
      // Month shown by each panel; null until derived from the range or tracked.
      panelMonths: null,
      focusedRange: [0, 0],
      // Bumped on selections that must re-focus the panels, even when the days
      // are the same as the ones already selected.
      focusNonce: 0,
      // Bumped to re-mount only the right panel when the left one pushes it.
      rightNonce: 0,
    };
    this.filterByDates = this.filterByDates.bind(this);
    this.changeCollapse = this.changeCollapse.bind(this);
  }

  solveLanguageCalendar = () => {
    const mapping = {
      es: localeEs,
      pt: localePt,
      en: defaultLocale,
    };
    const lang = localStorage.getItem('language');
    return mapping[lang];
  };

  // The abbreviated weekday names (`E`) of the Portuguese locale are the full
  // words, which overlap in the day columns, so it uses the short ones.
  solveWeekdayFormat = () =>
    localStorage.getItem('language') === 'pt' ? 'EEEEEE' : 'E';

  // `refocus` is false only for day clicks, which must leave the panels on the
  // month they show. Any other selection re-focuses them and restarts the pick.
  selectRangeDate = (item, refocus = true) => {
    // Normalize dates to ensure they are valid Date objects.
    const startDate = toValidDate(
      item.selection.startDate,
      getDefaultStartDate(),
    );
    const endDate = toValidDate(item.selection.endDate, getDefaultEndDate());

    // Set startDate to beginning of day in local time
    startDate.setHours(0, 0, 0, 0);

    // Set endDate to end of day in local time
    endDate.setHours(23, 59, 59, 999);

    // Ensure startDate is not after endDate
    const normalizedStartDate = startDate;
    const normalizedEndDate = endDate < startDate ? startDate : endDate;

    this.props.getFilterDates({
      start_value: normalizedStartDate,
      end_value: normalizedEndDate,
    });

    // Panel months must exist before the ranges change, or the panels would be
    // re-mounted on the new range instead of on the month they showed.
    const currentMonths = this.getPanelMonths();
    const focusedMonths = refocus
      ? getFocusMonths(
          normalizedStartDate,
          normalizedEndDate,
          new Date(),
          this.isSinglePanel(),
        )
      : null;

    this.setState((prev) => ({
      filterValues: {
        ...prev.filterValues,
        dates: [
          {
            ...item.selection,
            startDate: normalizedStartDate,
            endDate: normalizedEndDate,
          },
        ],
      },
      panelMonths: focusedMonths || prev.panelMonths || currentMonths,
      ...(refocus && {
        focusedRange: [0, 0],
        focusNonce: prev.focusNonce + 1,
      }),
    }));
  };

  handleStaticRangeChange = (item) => this.selectRangeDate(item);

  handleDayChange = (item) => this.selectRangeDate(item, false);

  handleRangeFocusChange = (focusedRange) => {
    this.setState({ focusedRange });
  };

  isSinglePanel = () => window.innerWidth < 1200;

  // Months shown by the panels: the tracked ones, or those derived from the
  // range that is currently displayed. Months tracked in the single panel
  // layout may break left < right and left <= current month once two panels
  // render (for example after a zoom change), so they are discarded in that case.
  getPanelMonths = () => {
    const single = this.isSinglePanel();
    const { panelMonths } = this.state;
    if (panelMonths && (single || isValidPanelMonths(panelMonths))) {
      return panelMonths;
    }
    const range = this.rangesCache?.ranges?.[0];
    return getFocusMonths(
      range ? range.startDate : getDefaultStartDate(),
      range ? range.endDate : getDefaultEndDate(),
      new Date(),
      single,
    );
  };

  // A month or year picked on the main calendar (the left panel, or the single
  // one) selects that whole month and restarts the pick. Both panels keep their
  // tracked months, so the selection is applied without re-focusing them. When
  // the left panel reaches or passes the right one's month, the right panel
  // moves to the month after it. A panel reads `shownDate` only when it mounts,
  // so the right panel is re-mounted (rightNonce) to show the new month.
  handleLeftShownChange = (date) => {
    const current = this.getPanelMonths();
    const left = startOfMonth(date);
    this.selectRangeDate(
      { selection: { ...getMonthRange(left), key: 'selection' } },
      false,
    );
    this.setState({ focusedRange: [0, 0] });
    this.setState((prev) => {
      const months = prev.panelMonths || current;
      const pushRight = !dayjs(left).isBefore(months.right, 'month');
      return {
        panelMonths: {
          ...months,
          left,
          ...(pushRight && { right: nextMonth(left) }),
        },
        ...(pushRight && { rightNonce: prev.rightNonce + 1 }),
      };
    });
  };

  handleRightShownChange = (date) => {
    const current = this.getPanelMonths();
    this.setState((prev) => ({
      panelMonths: {
        ...(prev.panelMonths || current),
        right: startOfMonth(date),
      },
    }));
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.resetFilter !== this.props.resetFilter &&
      this.props.resetFilter
    ) {
      this.selectRangeDate({
        selection: {
          startDate: null,
          endDate: null,
          key: 'selection',
        },
      });
    }

    // Sync state when rangeDefault or previewRange props change
    if (
      this.props.previewRange &&
      (prevProps.previewRange?.start !== this.props.previewRange.start ||
        prevProps.previewRange?.end !== this.props.previewRange.end)
    ) {
      const newStartDate = new Date(this.props.previewRange.start);
      const newEndDate = new Date(this.props.previewRange.end);

      // Only update if dates are different from current state
      const currentStart = this.state.filterValues.dates[0]?.startDate;
      const currentEnd = this.state.filterValues.dates[0]?.endDate;

      if (
        !currentStart ||
        !currentEnd ||
        newStartDate.getTime() !== new Date(currentStart).getTime() ||
        newEndDate.getTime() !== new Date(currentEnd).getTime()
      ) {
        this.selectRangeDate({
          selection: {
            startDate: newStartDate,
            endDate: newEndDate,
            key: 'selection',
          },
        });
      }
    }
  }

  componentDidMount() {
    if (this.props.rangeDefault) {
      this.selectRangeDate({
        selection: {
          startDate: new Date(this.props.rangeDefault.start),
          endDate: new Date(this.props.rangeDefault.end),
          key: 'selection',
        },
      });
    }
    if (typeof this.props.customFilter?.end_range === 'string') {
      // A range restored from the store is not a selection made on this screen:
      // the range stays empty so the button shows its label until the user
      // picks dates.
      this.setState({
        query: false,
        filterValues: {
          dates: [{ startDate: null, endDate: null, key: 'selection' }],
        },
      });
    }
  }

  restartDefaultDates = () => {
    if (this.props.previewRange) {
      const start = new Date(this.props.previewRange.start);
      const end = new Date(this.props.previewRange.end);
      this.setState((prev) => ({
        filterValues: {
          dates: [{ startDate: start, endDate: end, key: 'selection' }],
        },
        query: false,
        panelMonths: getFocusMonths(
          start,
          end,
          new Date(),
          this.isSinglePanel(),
        ),
        focusedRange: [0, 0],
        focusNonce: prev.focusNonce + 1,
      }));
    }
  };

  changeCollapse = () => {
    this.setState((prev) => ({ closeAfter: !prev.closeAfter }));
  };

  // react-date-range re-focuses the calendar on the range start whenever the
  // `ranges` prop identity changes, so keep the same array while the selected
  // days (not the milliseconds) are unchanged.
  getStableRanges = (ranges) => {
    const signature = ranges
      .map(
        ({ startDate, endDate, key }) =>
          `${key}:${dayjs(startDate).format('YYYY-MM-DD')}:${dayjs(
            endDate,
          ).format('YYYY-MM-DD')}`,
      )
      .join('|');
    if (this.rangesCache?.signature !== signature) {
      this.rangesCache = { signature, ranges };
    }
    return this.rangesCache.ranges;
  };

  displayRangeFilter = (date1, date2) => {
    return `${I18n.t('dashboard.from')} ${formatDate(
      date1,
      localStorage.getItem('language'),
    )} ${I18n.t('dashboard.to')} ${formatDate(
      date2,
      localStorage.getItem('language'),
    )}`;
  };

  getPopupMessage = (date1, date2) => {
    if (!date1) return I18n.t('dashboard.lastMonthMessage');
    if (this.isLastWeek(date1, date2)) {
      return I18n.t('dashboard.lastWeekMessage');
    }
    return I18n.t('dashboard.lastMonthMessage');
  };

  isLastMonth = (date1, date2) => {
    const endDate = toValidDate(this.props.end_date, getDefaultEndDate());
    const prevMonth = toValidDate(this.props.start_date, getDefaultStartDate());
    let newdate1 = toValidDate(date1, prevMonth);
    let newdate2 = toValidDate(date2, endDate);
    return equalsDate(newdate2, endDate) && equalsDate(newdate1, prevMonth);
  };

  isLastWeek = (date1, date2) => {
    const endDate = toValidDate(this.props.end_date, getDefaultEndDate());
    const prevWeek = lastWeek(endDate);
    return (
      equalsDate(toValidDate(date2, endDate), endDate) &&
      equalsDate(toValidDate(date1, prevWeek), prevWeek)
    );
  };

  filterByDates = () => {
    this.props.setCreditFilterClear(false);
    const { startDate, endDate: filterEndDate } =
      this.state.filterValues.dates[0];
    const endDate = toValidDate(this.props.end_date, getDefaultEndDate());
    const prevMonth = toValidDate(this.props.start_date, getDefaultStartDate());
    const filterStart = startDate || prevMonth;
    const filterEnd = filterEndDate || endDate;
    const filter = { start_range: filterStart, end_range: filterEnd };
    this.setState({
      filterValues: {
        dates: [
          {
            startDate: dayjs(filter.start_range).toISOString(),
            endDate: dayjs(filter.end_range).toISOString(),
            key: 'selection',
          },
        ],
      },
    });
    this.props.filter(filter);

    this.changeCollapse();
  };
  handleShow = (e) => {
    if (
      e.path.filter((element) => element.id === 'popup-button').length === 0
    ) {
      this.setState({ hiddenPopup: true });
    }
  };
  handleHidden = () => {
    this.setState({ hiddenPopup: false });
  };

  getHistoricalStartDate = () => {
    return toValidDate(this.props.start_date, getDefaultStartDate());
  };

  // handleCalendarOpen = () => {
  //   this.setState({
  //     ...this.state,
  //     isCalendarOpen: !this.state.isCalendarOpen,
  //   });
  // };

  render() {
    const endDate = toValidDate(
      this.props.credits_end_date || this.props.end_date,
      getDefaultEndDate(),
    );
    const startDate = toValidDate(
      this.props.start_date
        ? this.props.start_date
        : this.props.credits_start_date,
      getDefaultStartDate(),
    );
    const { startDate: startFilter, endDate: endFilter } =
      this.state.filterValues.dates[0];
    const initDate = startFilter
      ? toValidDate(startFilter, startDate)
      : startDate;
    const lastDate = endFilter ? toValidDate(endFilter, endDate) : endDate;
    const isRangeSelected = (range, expectedStart, expectedEnd) => {
      const selectedStart = toValidDate(range?.startDate, expectedStart);
      const selectedEnd = toValidDate(range?.endDate, expectedEnd);

      return (
        equalsDate(selectedStart, expectedStart) &&
        equalsDate(selectedEnd, expectedEnd)
      );
    };
    const staticRanges = [
      {
        label: I18n.t('dashboard.lastWeek'),
        range: () => ({
          startDate: lastWeek(endDate),
          endDate: endDate,
        }),
        isSelected: (range) => {
          const expectedStart = lastWeek(endDate);
          const expectedEnd = endDate;
          return isRangeSelected(range, expectedStart, expectedEnd);
        },
      },
      {
        label: I18n.t('dashboard.lastMonth'),
        range: () => ({
          startDate: lastMonth(endDate),
          endDate: endDate,
        }),
        isSelected: (range) => {
          const expectedStart = lastMonth(endDate);
          const expectedEnd = endDate;
          return isRangeSelected(range, expectedStart, expectedEnd);
        },
      },
      {
        label: I18n.t('dashboard.all'),
        range: () => ({
          startDate: this.getHistoricalStartDate(),
          endDate: endDate,
        }),
        isSelected: (range) => {
          const expectedStart = toValidDate(
            this.getHistoricalStartDate(),
            startDate,
          );
          const expectedEnd = endDate;
          return isRangeSelected(range, expectedStart, expectedEnd);
        },
      },
    ];
    const ranges = this.getStableRanges(
      startFilter && endFilter
        ? this.state.filterValues.dates.map((range) => ({
            ...range,
            startDate: toValidDate(range.startDate, startDate),
            endDate: toValidDate(range.endDate, endDate),
          }))
        : [
            {
              ...this.state.filterValues.dates[0],
              startDate,
              endDate,
            },
          ],
    );
    const pickerKey = `${this.rangesCache.signature}|${this.state.focusNonce}`;
    const sharedPanelProps = {
      locale: this.solveLanguageCalendar(),
      weekdayDisplayFormat: this.solveWeekdayFormat(),
      ranges,
      focusedRange: this.state.focusedRange,
      onRangeFocusChange: this.handleRangeFocusChange,
      onChange: this.handleDayChange,
      navigatorRenderer: renderMonthYearNavigator,
      showSelectionPreview: true,
      moveRangeOnFirstSelection: false,
      months: 1,
      direction: 'horizontal',
      editableDateInputs: false,
      showDateDisplay: false,
    };
    const today = new Date();
    const panelMonths = this.getPanelMonths();
    // The library re-focuses a calendar on the range start whenever `ranges`
    // changes, so the panels are re-mounted (key) on the month they track.
    const bounds = getPanelBounds(panelMonths, today);
    const panels = this.isSinglePanel() ? (
      <DateRange
        key={`single|${pickerKey}`}
        {...sharedPanelProps}
        maxDate={today}
        shownDate={panelMonths.left}
        onShownDateChange={this.handleLeftShownChange}
      />
    ) : (
      [
        <DateRange
          key={`left|${pickerKey}`}
          {...sharedPanelProps}
          {...bounds.left}
          shownDate={panelMonths.left}
          onShownDateChange={this.handleLeftShownChange}
        />,
        <DateRange
          key={`right|${pickerKey}|${this.state.rightNonce}`}
          {...sharedPanelProps}
          {...bounds.right}
          shownDate={panelMonths.right}
          onShownDateChange={this.handleRightShownChange}
        />,
      ]
    );

    return (
      <div className={`filter-date ${this.props.class}`}>
        <DropdownMenu
          name="filter-home"
          className={`${
            (this.state.filterValues.dates[0].startDate !== '' &&
              this.state.filtered) ||
            this.state.filtered ||
            this.props.didFilter
              ? 'active'
              : ''
          } ${this.state.openCalendar ? 'open-calendar' : ''} date-button`}
          closeAfter={this.state.closeAfter}
          closeAfterAction={this.changeCollapse}
          dropCalendar={{ startDate, initDate, endDate, lastDate }}
          query={this.state.query}
          loading={this.props.dateStatus?.loading}
          label={this.displayRangeFilter(initDate, lastDate)}
          restartState={this.restartDefaultDates}
          resetFilter={this.props.resetFilter}
          resetInCredits={this.props.resetInCredits}
          inCredits={this.props.inCredits}
          filtering={this.props.filtering}
          historyFiltering={this.props.historyFiltering}
          filteringDate={this.props.filteringDate}
          jobDateFiltering={this.props.jobDateFiltering}
          handleCalendarOpen={this.handleCalendarOpen}
          onDashboard={this.props.cities ? true : false}
          onCredits={this.props.onCredits}
          location={this.props.location}
          onClick={() => {
            this.setState((prev) => ({ openCalendar: !prev.openCalendar }));
          }}
          user={this.props.user}
          nestedComponents={[
            <div
              id="wrapper-date-picker-range"
              className="wrapper-date-picker-range"
            >
              <div className="rdrDateRangePickerWrapper">
                <DefinedRange
                  ranges={ranges}
                  focusedRange={this.state.focusedRange}
                  onChange={this.handleStaticRangeChange}
                  staticRanges={staticRanges}
                  inputRanges={[]}
                />
                {panels}
              </div>
              <Button
                className="apply-date-button"
                text={I18n.t('dashboard.filter')}
                onClick={() => {
                  this.filterByDates();
                  this.setState({
                    openCalendar: false,
                    filtered: true,
                    query: true,
                  });
                }}
              />
            </div>,
          ]}
        />
        {!this.props.hiddenPopup ? (
          <Tooltip
            message={this.getPopupMessage(startFilter, endFilter)}
            translate={false}
            materialIcon="contact_support"
            className="dateRangeFilter"
            right
          />
        ) : (
          <div className="space" />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  filterDates: state.common.filterDates,
  user: state.session.user,
});

const mapDispatchToProps = (dispatch) => ({
  getFilterDates: (filterDates) =>
    dispatch(actions.common.getFilterDates(filterDates)),
  setCreditFilterClear: (value) =>
    dispatch(actions.common.setCreditFilterClear(value)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(DateRangePickerFilter));
